import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { Option } from 'src/components/SelectBox';
import { timeOptions, timezoneOptions } from './constants';
import { formatTimeLabel } from 'src/utils/date';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import type { WorkflowContactWindow } from 'src/interfaces/workflows.interface';
import { useNotification } from 'src/contexts/NotificationContext';
import { useLocation } from 'react-router-dom';

type ContactWindowContextValue = {
  label: string;
  description: string;
  setLabel: (label: string) => void;
  setDescription: (description: string) => void;

  selectedDays: number[];
  toggleDay: (dayValue: number) => void;
  setSelectedDays: (days: number[]) => void;

  startTime: Option;
  endTime: Option;
  setStartTime: (option: Option) => void;
  setEndTime: (option: Option) => void;

  primaryTz: Option;
  fallbackTz: Option;
  setPrimaryTz: (option: Option) => void;
  setFallbackTz: (option: Option) => void;

  useHolidays: boolean;
  setUseHolidays: (use: boolean) => void;

  blackoutDates: string[];
  setBlackoutDates: React.Dispatch<React.SetStateAction<string[]>>;
};

const ContactWindowContext = createContext<
  ContactWindowContextValue | undefined
>(undefined);

type ContactWindowProviderProps = {
  children: React.ReactNode;
};

export const ContactWindowProvider: React.FC<ContactWindowProviderProps> = ({
  children,
}) => {
  const [label, setLabel] = useState<string>('Contact window');
  const [description, setDescription] = useState<string>('');
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startTime, setStartTime] = useState<Option>(timeOptions[18]);
  const [endTime, setEndTime] = useState<Option>(timeOptions[38]);
  const [primaryTz, setPrimaryTz] = useState<Option>(timezoneOptions[0]);
  const [fallbackTz, setFallbackTz] = useState<Option>(timezoneOptions[1]);
  const [useHolidays, setUseHolidays] = useState(true);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);

  const toggleDay = (dayValue: number) => {
    setSelectedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((v) => v !== dayValue)
        : [...prev, dayValue],
    );
  };

  const value: ContactWindowContextValue = useMemo(
    () => ({
      label,
      description,
      setLabel,
      setDescription,
      selectedDays,
      toggleDay,
      setSelectedDays,
      startTime,
      endTime,
      setStartTime,
      setEndTime,
      primaryTz,
      fallbackTz,
      setPrimaryTz,
      setFallbackTz,
      useHolidays,
      setUseHolidays,
      blackoutDates,
      setBlackoutDates,
    }),
    [
      label,
      description,
      selectedDays,
      startTime,
      endTime,
      primaryTz,
      fallbackTz,
      useHolidays,
      blackoutDates,
    ],
  );

  return (
    <ContactWindowContext.Provider value={value}>
      {children}
    </ContactWindowContext.Provider>
  );
};

type UseContactWindowReturn = ContactWindowContextValue & {
  selectedDaysText: string;
  apply: () => Promise<boolean>;
  isInitializing: boolean;
};

export const useContactWindow = (): UseContactWindowReturn => {
  const ctx = useContext(ContactWindowContext);
  if (!ctx) {
    throw new Error(
      'useContactWindow must be used within ContactWindowProvider',
    );
  }

  const {
    setLabel: setCtxLabel,
    setDescription: setCtxDescription,
    setSelectedDays: setCtxSelectedDays,
    setStartTime: setCtxStartTime,
    setEndTime: setCtxEndTime,
    setPrimaryTz: setCtxPrimaryTz,
    setFallbackTz: setCtxFallbackTz,
    setUseHolidays: setCtxUseHolidays,
    setBlackoutDates: setCtxBlackoutDates,
  } = ctx;

  const [contactWindowId, setContactWindowId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const { get, post, patch } = useAuthedRequest();
  const notification = useNotification();
  const location = useLocation();

  const toTimeLabel = (value: string) => formatTimeLabel(value);

  const applyStartTime = (record: WorkflowContactWindow) => {
    if (record.startTime) {
      const found = timeOptions.find((o) => o.value === record.startTime);
      const option: Option = found || {
        label: toTimeLabel(record.startTime),
        value: record.startTime,
      };
      setCtxStartTime(option);
    }
  };

  const applyEndTime = (record: WorkflowContactWindow) => {
    if (record.endTime) {
      const found = timeOptions.find((o) => o.value === record.endTime);
      const option: Option = found || {
        label: toTimeLabel(record.endTime),
        value: record.endTime,
      };
      setCtxEndTime(option);
    }
  };

  const applyPrimaryTz = (record: WorkflowContactWindow) => {
    if (record.primaryTz) {
      const found = timezoneOptions.find((o) => o.value === record.primaryTz);
      const option: Option = found || {
        label: record.primaryTz,
        value: record.primaryTz,
      };
      setCtxPrimaryTz(option);
    }
  };

  const applyFallbackTz = (record: WorkflowContactWindow) => {
    if (record.fallbackTz) {
      const found = timezoneOptions.find((o) => o.value === record.fallbackTz);
      const option: Option = found || {
        label: record.fallbackTz,
        value: record.fallbackTz,
      };
      setCtxFallbackTz(option);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(location.search);
        const requestedId = params.get('id');
        if (!requestedId) {
          return;
        }

        const selectedRecord = await get(
          `/workflow_contact_windows/${requestedId}`,
        );

        setContactWindowId(selectedRecord.id);
        setCtxLabel(selectedRecord.label);
        setCtxDescription(selectedRecord.description);
        setCtxSelectedDays(Array.from(new Set(selectedRecord.days)));
        applyStartTime(selectedRecord.startTime);
        applyEndTime(selectedRecord.endTime);
        applyPrimaryTz(selectedRecord.primaryTz);
        applyFallbackTz(selectedRecord.fallbackTz);
        setCtxUseHolidays(selectedRecord.usHolidays);
        setCtxBlackoutDates(selectedRecord.blackoutDates);
      } catch {
        // No-op; notification on apply
      } finally {
        setIsInitializing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable across renders
  }, [location.search]);

  const selectedDaysText = useMemo(() => {
    const labels = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];
    return ctx.selectedDays.map((d) => labels[d]).join(', ');
  }, [ctx.selectedDays]);

  const apply = useCallback(async (): Promise<boolean> => {
    try {
      const body = {
        label: ctx.label,
        description: ctx.description,
        days: ctx.selectedDays,
        startTime: ctx.startTime.value,
        endTime: ctx.endTime.value,
        primaryTz: ctx.primaryTz.value,
        fallbackTz: ctx.fallbackTz.value,
        usHolidays: ctx.useHolidays,
        blackoutDates: ctx.blackoutDates,
      };

      if (contactWindowId) {
        await patch(`/workflow_contact_windows/${contactWindowId}`, body);
      } else {
        const created = await post(`/workflow_contact_windows`, body);
        if (created?.id) setContactWindowId(created.id);
      }
      notification.success('Contact window updated');
      return true;
    } catch {
      notification.error('Failed to update contact window');
      return false;
    }
  }, [
    contactWindowId,
    ctx.label,
    ctx.description,
    ctx.selectedDays,
    ctx.startTime.value,
    ctx.endTime.value,
    ctx.primaryTz.value,
    ctx.fallbackTz.value,
    ctx.useHolidays,
    ctx.blackoutDates,
    patch,
    post,
    notification,
  ]);

  return {
    ...ctx,
    selectedDaysText,
    apply,
    isInitializing,
  };
};
